import { useState } from 'react';
import PropTypes from 'prop-types';
import { ChartBarIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { MONTH_NAMES, UNIT_COLOR_FALLBACK, UNIT_COLOR_MAP } from '@/utils/gconUtils';

const COMPONENT_COLORS = { C1: '#2D7CFF', C2: '#00A99D', C3: '#FF8F12', C4: '#A694FF' };
// Years without a GCON Input only have a production total (from the Intensity sheet)
const componentColor = c => COMPONENT_COLORS[c] ?? '#64748b';

const fmtTonnes = v => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(2)} Mt`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)} kt`;
  return `${v.toFixed(0)} t`;
};
const fmtTonnesFull = v => `${Math.round(v).toLocaleString()} t`;
const sum = vals => vals.reduce((a, b) => a + b, 0);

const StackedBars = ({ months, series, fmtMonth, multiYear }) => {
  const totals = months.map((_, i) => sum(series.map(s => s.values[i] ?? 0)));
  const max = Math.max(...totals, 1);
  const VW = 820,
    VH = 220,
    ML = 56,
    MB = multiYear ? 36 : 28,
    MT = 8;
  const chartW = VW - ML - 8;
  const chartH = VH - MB - MT;
  const n = months.length;
  const barW = Math.max(chartW / n - 2, 3);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({ val: max * f, y: VH - MB - f * chartH }));

  return (
    <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full" style={{ height: VH }}>
      {yTicks.map(({ val, y }) => (
        <g key={y}>
          <line x1={ML} x2={VW - 8} y1={y} y2={y} stroke="#f0f0f0" strokeWidth="1" />
          <text x={ML - 4} y={y + 4} fontSize="9" fill="#9ca3af" textAnchor="end">
            {fmtTonnes(val)}
          </text>
        </g>
      ))}
      {months.map((month, mi) => {
        const bx = ML + (mi / n) * chartW + (chartW / n - barW) / 2;
        let cum = 0;
        return (
          <g key={month}>
            {series.map(s => {
              const val = s.values[mi] ?? 0;
              if (val <= 0) return null;
              const h = (val / max) * chartH;
              const y = VH - MB - cum - h;
              cum += h;
              return (
                <rect key={s.key} x={bx} y={y} width={barW} height={h} fill={s.color} opacity={0.88} rx="1">
                  <title>{`${s.label} · ${fmtMonth(month)}: ${fmtTonnesFull(val)}`}</title>
                </rect>
              );
            })}
            <text x={bx + barW / 2} y={VH - MB + 12} fontSize="8.5" fill="#6b7280" textAnchor="middle">
              {fmtMonth(month)}
            </text>
            {multiYear && month.endsWith('-01') && mi > 0 && (
              <line x1={bx - 1} x2={bx - 1} y1={MT} y2={VH - MB} stroke="#e5e7eb" strokeDasharray="3 3" />
            )}
          </g>
        );
      })}
      <line x1={ML} x2={ML} y1={MT} y2={VH - MB} stroke="#e5e7eb" />
      <line x1={ML} x2={VW - 8} y1={VH - MB} y2={VH - MB} stroke="#e5e7eb" />
    </svg>
  );
};

StackedBars.propTypes = {
  months: PropTypes.arrayOf(PropTypes.string).isRequired,
  series: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string.isRequired,
      values: PropTypes.arrayOf(PropTypes.number).isRequired,
    }),
  ).isRequired,
  fmtMonth: PropTypes.func.isRequired,
  multiYear: PropTypes.bool,
};

/**
 * Monthly C1–C4 production per unit — the denominators behind the intensity
 * and B.O.P. figures, shown so users can check them against their own records.
 */
const ProductionView = ({ data, reportingYears }) => {
  const [groupBy, setGroupBy] = useState('unit'); // unit | component
  const [hidden, setHidden] = useState([]); // locations toggled off

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 px-8 text-center">
        <InformationCircleIcon className="w-10 h-10" />
        <p className="text-sm">This analysis was saved before the production breakdown was added.</p>
        <p className="text-xs">Re-upload its files to see production by unit and component.</p>
      </div>
    );
  }
  if (!data.units.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2 px-8 text-center">
        <ChartBarIcon className="w-10 h-10" />
        <p className="text-sm">No production data found.</p>
        <p className="text-xs">
          Ensure the Input files contain an <em>export_production</em> sheet.
        </p>
      </div>
    );
  }

  const { months, components, units } = data;
  const multiYear = reportingYears?.length > 1;
  const fmtMonth = key => {
    const [y, m] = key.split('-');
    const label = MONTH_NAMES[parseInt(m, 10) - 1];
    return multiYear && m === '01' ? `${label} '${y.slice(2)}` : label;
  };
  const colorOf = (u, i) => UNIT_COLOR_MAP[u.unit] ?? UNIT_COLOR_FALLBACK[i % UNIT_COLOR_FALLBACK.length];

  const shown = units.filter(u => !hidden.includes(u.location));
  const toggleUnit = loc =>
    setHidden(prev => (prev.includes(loc) ? prev.filter(l => l !== loc) : shown.length > 1 ? [...prev, loc] : prev));

  const series =
    groupBy === 'unit'
      ? shown.map(u => ({
          key: u.location,
          label: u.unit,
          color: colorOf(u, units.indexOf(u)),
          values: u.total,
        }))
      : components.map(c => ({
          key: c,
          label: c,
          color: componentColor(c),
          values: months.map((_, i) => sum(shown.map(u => u.components[c]?.[i] ?? 0))),
        }));

  const monthTotals = months.map((_, i) => sum(series.map(s => s.values[i] ?? 0)));
  const grandTotal = sum(monthTotals);
  const largest = [...shown].sort((a, b) => sum(b.total) - sum(a.total))[0];
  const peakIdx = monthTotals.indexOf(Math.max(...monthTotals));
  const componentTotal = c => sum(shown.map(u => sum(u.components[c] ?? [])));

  return (
    <div className="p-6 overflow-y-auto h-full space-y-5">
      {/* KPI strip */}
      <div className="flex gap-3">
        {[
          { label: 'Total production', value: fmtTonnes(grandTotal), sub: `${months.length} months · C1–C4` },
          {
            label: 'Largest unit',
            value: largest?.unit ?? '—',
            sub: largest && grandTotal > 0 ? `${((sum(largest.total) / grandTotal) * 100).toFixed(1)}% of total` : '—',
          },
          {
            label: 'Peak month',
            value: months[peakIdx] ? fmtMonth(months[peakIdx]) : '—',
            sub: fmtTonnes(monthTotals[peakIdx] ?? 0),
          },
        ].map(s => (
          <div key={s.label} className="flex-1 bg-white border border-gray-100 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
            <p className="text-sm font-semibold text-gray-800">{s.value}</p>
            <p className="text-xs text-gray-400">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div className="bg-white border border-gray-100 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Monthly production by {groupBy === 'unit' ? 'unit' : 'component'} (t)
          </p>
          <div className="join">
            {[
              { key: 'unit', label: 'By Unit' },
              { key: 'component', label: 'By Component' },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                className={`join-item btn btn-xs ${groupBy === opt.key ? 'btn-primary' : 'btn-ghost'}`}
                onClick={() => setGroupBy(opt.key)}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <StackedBars months={months} series={series} fmtMonth={fmtMonth} multiYear={multiYear} />

        {/* Unit filter doubles as the legend in unit mode */}
        <div className="flex flex-wrap items-center gap-2 mt-3">
          <span className="text-xs text-gray-400">Units:</span>
          {units.map((u, i) => {
            const on = !hidden.includes(u.location);
            return (
              <button
                key={u.location}
                type="button"
                onClick={() => toggleUnit(u.location)}
                className={`text-xs px-2 py-0.5 rounded-full border flex items-center gap-1.5 transition-colors ${
                  on ? 'border-gray-300 text-gray-700' : 'border-gray-100 text-gray-300 line-through'
                }`}
              >
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: on ? colorOf(u, i) : '#e5e7eb' }} />
                {u.unit}
              </button>
            );
          })}
          {groupBy === 'component' &&
            components.map(c => (
              <span key={c} className="text-xs text-gray-600 flex items-center gap-1.5 ml-2">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: componentColor(c) }} />
                {c}
              </span>
            ))}
        </div>
      </div>

      {/* Unit × component totals */}
      <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Unit</th>
              {components.map(c => (
                <th key={c} className="text-right px-4 py-2.5 text-gray-500 font-medium">
                  {c}
                </th>
              ))}
              <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Total</th>
              <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Share</th>
            </tr>
          </thead>
          <tbody>
            {shown.map(u => {
              const total = sum(u.total);
              return (
                <tr key={u.location} className="border-b border-gray-50 hover:bg-gray-50">
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                        style={{ background: colorOf(u, units.indexOf(u)) }}
                      />
                      <span className="font-medium text-gray-700">{u.unit}</span>
                      {!u.in_category_benchmark && (
                        <span
                          className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500"
                          title="Left out of the plant-wide production used for the category B.O.P. benchmark"
                        >
                          not in category B.O.P.
                        </span>
                      )}
                    </div>
                  </td>
                  {components.map(c => (
                    <td key={c} className="px-4 py-2.5 text-right text-gray-600 tabular-nums">
                      {fmtTonnesFull(sum(u.components[c] ?? []))}
                    </td>
                  ))}
                  <td className="px-4 py-2.5 text-right font-medium text-gray-800 tabular-nums">
                    {fmtTonnesFull(total)}
                  </td>
                  <td className="px-4 py-2.5 text-right text-gray-500 tabular-nums">
                    {grandTotal > 0 ? `${((total / grandTotal) * 100).toFixed(1)}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot className="bg-gray-50 border-t border-gray-100">
            <tr>
              <td className="px-4 py-2.5 font-semibold text-gray-700">Total</td>
              {components.map(c => (
                <td key={c} className="px-4 py-2.5 text-right font-semibold text-gray-700 tabular-nums">
                  {fmtTonnesFull(componentTotal(c))}
                </td>
              ))}
              <td className="px-4 py-2.5 text-right font-semibold text-gray-800 tabular-nums">
                {fmtTonnesFull(grandTotal)}
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Month-by-month values for whatever the chart is showing */}
      <div className="bg-white border border-gray-100 rounded-lg overflow-x-auto">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide px-4 pt-3 pb-2">
          Monthly values by {groupBy === 'unit' ? 'unit' : 'component'}
        </p>
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-y border-gray-100">
            <tr>
              <th className="text-left px-4 py-2 text-gray-500 font-medium">Month</th>
              {series.map(s => (
                <th key={s.key} className="text-right px-4 py-2 text-gray-500 font-medium whitespace-nowrap">
                  {s.label}
                </th>
              ))}
              <th className="text-right px-4 py-2 text-gray-500 font-medium">Total</th>
            </tr>
          </thead>
          <tbody>
            {months.map((month, i) => (
              <tr key={month} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-4 py-2 font-medium text-gray-700 whitespace-nowrap">
                  {MONTH_NAMES[parseInt(month.split('-')[1], 10) - 1]} {month.split('-')[0]}
                </td>
                {series.map(s => (
                  <td key={s.key} className="px-4 py-2 text-right text-gray-600 tabular-nums">
                    {fmtTonnesFull(s.values[i] ?? 0)}
                  </td>
                ))}
                <td className="px-4 py-2 text-right font-medium text-gray-800 tabular-nums">
                  {fmtTonnesFull(monthTotals[i])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ProductionView.propTypes = {
  data: PropTypes.shape({
    months: PropTypes.arrayOf(PropTypes.string).isRequired,
    components: PropTypes.arrayOf(PropTypes.string).isRequired,
    units: PropTypes.arrayOf(PropTypes.object).isRequired,
  }),
  reportingYears: PropTypes.arrayOf(PropTypes.number),
};

export default ProductionView;
